import { tx } from '@transifex/native';
import { Outlet, useLoaderData, type ShouldRevalidateFunctionArgs } from 'react-router';
import { getLocale } from '~/lib/languages';
import { useJapaneseFonts } from '~/hooks/use-japanese-fonts';
import type { Route } from './+types/translation-container';
import { langMiddleware } from '~/middleware/lang';
import { env } from 'cloudflare:workers';
import { SentryServer } from '~/lib/sentry.server';

export const middleware = [langMiddleware];

// check the lang cookie - revalidate if it changes
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  const currentUrl = new URL(args.currentUrl);
  const nextUrl = new URL(args.nextUrl);
  const currentLang = currentUrl.searchParams.get('lang');
  const nextLang = nextUrl.searchParams.get('lang');

  if (nextLang && currentLang !== nextLang) {
    return true;
  }
  return false;
}

export async function loader(args: Route.LoaderArgs) {
  const locale = await getLocale(args.request);

  try {
    tx.init({ token: env.TRANSIFEX_TOKEN });

    await tx.fetchTranslations(locale);
    const translations = tx.cache.getTranslations(locale);

    return {
      translations,
      locale,
      token: env.TRANSIFEX_TOKEN,
    };
  } catch (error) {
    if (env.ENVIRONMENT !== 'preview') {
      SentryServer.captureException(error);
    } else {
      console.error(error);
    }
    return {
      translations: {},
      locale,
      token: '',
    };
  }
}

export default function AuthContainer() {
  const { translations, locale, token } = useLoaderData<typeof loader>();

  tx.init({
    currentLocale: locale,
    token,
  });
  if (locale) {
    tx.cache.update(locale, translations);
  }

  useJapaneseFonts(locale);

  return <Outlet />;
}
